import { ButtonBase, CircularProgress, IconButton } from '@mui/material';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { FreeMode, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import BlocksView from './BlocksView';
import HomesView from './HomesView';
import StagesView from './StagesView';
import { useShowroom } from 'context';
import EntrancesView from './EntrancesView';

const ShaxmatkaVisualView = ({ toggleHomeItem = () => {} }) => {
  const { t } = useTranslation();
  const [, dispatch] = useShowroom();
  const [searchParams] = useSearchParams();
  const [prevObjectId, setPrevObjectId] = useState(
    searchParams.get('objectId'),
  );
  const [sliderActiveIndex, setSliderActiveIndex] = useState(0);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [selectedStage, setSelectedStage] = useState(null);
  const [selectedEntrance, setSelectedEntrance] = useState(null);
  const [step, setStep] = useState('block');
  const axiosPrivate = useAxiosPrivate();
  const swiperRef = useRef(null);

  const [blocksVisualQuery, stagesVisualQuery, homesVisualQuery] = useQueries([
    {
      queryKey: ['shaxmatkaBlocksVisualView', searchParams.get('objectId')],
      queryFn: async function () {
        const response = await axiosPrivate.get(
          `/admin/visual/object/${searchParams.get('objectId')}`,
        );
        return response.data.data;
      },
      enabled: Boolean(searchParams.get('objectId')),
      retry: false,
    },
    {
      queryKey: [
        'shaxmatkaStagesVisualView',
        selectedBlock?.id,
        selectedEntrance?.id,
      ],
      queryFn: async function () {
        if (selectedBlock?.id) {
          const response = await axiosPrivate.get(
            `/admin/visual/block/${selectedBlock?.id}${selectedEntrance?.id ? '?padezd_id=' + selectedEntrance?.id : ''}`,
          );
          return response.data.data;
        }
        return [];
      },
      enabled: Boolean(selectedBlock?.id) && step == 'stages',
      retry: false,
    },
    {
      queryKey: [
        'shaxmatkaHomesVisualView',
        selectedStage,
        selectedEntrance?.id,
      ],
      queryFn: async function () {
        if (selectedBlock?.id && selectedStage) {
          const response = await axiosPrivate.get(
            `/admin/visual/homes/${selectedBlock?.id}?stage=${selectedStage}${selectedEntrance?.id ? '&padezd_id=' + selectedEntrance?.id : ''}`,
          );
          return response.data.data;
        }
        return [];
      },

      enabled: Boolean(selectedStage) && step == 'homes',
      retry: false,
    },
  ]);

  const hasEntrancePolygon = useMemo(() => {
    if (!blocksVisualQuery?.data?.blocks?.length || !selectedBlock?.id)
      return false;
    const block = blocksVisualQuery?.data?.blocks?.find(
      (block) => block?.id == selectedBlock?.id,
    );
    return block?.padezdimage;
  }, [blocksVisualQuery.data, selectedBlock?.id]);

  const [entrancesVisualQuery] = useQueries([
    {
      queryKey: [
        'shaxmatkaEntranceVisualView',
        selectedBlock?.id,
        hasEntrancePolygon,
      ],
      queryFn: async function () {
        if (selectedBlock?.id) {
          const response = await axiosPrivate.get(
            `/admin/visual/padezds/${selectedBlock?.id}`,
          );
          return response.data.data;
        }
        return [];
      },
      enabled: Boolean(selectedBlock?.id) && hasEntrancePolygon,
      retry: false,
    },
  ]);

  useEffect(() => {
    if (
      searchParams.get('objectId') &&
      searchParams.get('objectId') != prevObjectId
    ) {
      setPrevObjectId(searchParams.get('objectId'));
      blocksVisualQuery.refetch();
    }
  }, [searchParams]);

  const handleBackFromStages = () => {
    dispatch({ type: 'setHomeId', payload: null });
    setStep(hasEntrancePolygon ? 'entrances' : 'blocks');
  };

  const handleBackFromHomes = () => {
    setSelectedStage(null);
    dispatch({ type: 'setHomeId', payload: null });
    setStep('stages');
  };

  return (
    <div className='sheet-wrapper !h-full'>
      <div className='sheet-base-area relative !h-full'>
        {blocksVisualQuery.isLoading ||
        blocksVisualQuery.isFetching ||
        entrancesVisualQuery.isLoading ||
        entrancesVisualQuery.isFetching ||
        stagesVisualQuery.isLoading ||
        stagesVisualQuery.isFetching ? (
          <div className='circular-progress-box min-h-[500px] h-full w-full'>
            <CircularProgress size={50} />
          </div>
        ) : (
          <>
            {step == 'homes' ? (
              <div className='shaxmatka-visual-view-homes-wrapper !h-full'>
                <div className='stages-swiper-wrapper !h-full'>
                  <div className='stages-swiper-body !h-[calc(100%-20px)]'>
                    <Swiper
                      lazy='true'
                      direction='vertical'
                      freeMode={true}
                      slidesPerView={8}
                      spaceBetween={12}
                      pagination={false}
                      modules={[FreeMode, Pagination]}
                      onBeforeInit={(swiper) => {
                        swiperRef.current = swiper;
                      }}
                      onSlideChange={(a) => {
                        setSliderActiveIndex(a?.activeIndex);
                      }}
                      className='h-full !pb-2 !pt-1'
                    >
                      {stagesVisualQuery.data.blocks &&
                        stagesVisualQuery.data.blocks.length > 0 &&
                        stagesVisualQuery.data.blocks.map((block, index) => (
                          <SwiperSlide
                            key={`swiper-item-${index}`}
                            className='min-h-[60px]'
                          >
                            <div className='swiper-stage-item'>
                              <ButtonBase
                                className={`stage-btn${
                                  selectedStage == block?.stage
                                    ? ' is-selected'
                                    : ''
                                }`}
                                onClick={() => {
                                  setSelectedStage(block?.stage);
                                  toggleHomeItem(null, null);
                                }}
                              >
                                {block?.stage}
                              </ButtonBase>
                            </div>
                          </SwiperSlide>
                        ))}
                    </Swiper>
                    <div
                      className={`swiper-navigation-button-wrapper prev${
                        sliderActiveIndex == 0 ? ' is-disabled' : ''
                      }`}
                    >
                      <IconButton
                        variant='swiperNavigationButton'
                        color='primary'
                        onClick={() => {
                          swiperRef.current?.slidePrev();
                        }}
                        disabled={sliderActiveIndex == 0}
                        size='small'
                      >
                        <i className='bi bi-chevron-left' />
                      </IconButton>
                    </div>
                    <div
                      className={`swiper-navigation-button-wrapper next${
                        sliderActiveIndex ==
                        stagesVisualQuery.data.blocks.length - 1
                          ? ' is-disabled'
                          : ''
                      }`}
                    >
                      <IconButton
                        variant='swiperNavigationButton'
                        color='primary'
                        onClick={() => {
                          swiperRef.current?.slideNext();
                        }}
                        disabled={
                          sliderActiveIndex ==
                          stagesVisualQuery.data.blocks.length - 1
                        }
                        size='small'
                      >
                        <i className='bi bi-chevron-right' />
                      </IconButton>
                    </div>
                  </div>
                </div>
                <div className='homes-view-wrapper'>
                  {homesVisualQuery.isLoading || homesVisualQuery.isFetching ? (
                    <div className='circular-progress-box min-h-[500px] h-full w-full'>
                      <CircularProgress size={35} />
                    </div>
                  ) : homesVisualQuery.data && homesVisualQuery.data?.id ? (
                    <HomesView
                      data={homesVisualQuery.data}
                      stagesData={stagesVisualQuery.data}
                      backFn={handleBackFromHomes}
                      selectedBlockId={selectedBlock}
                      selectedStage={selectedStage}
                      onClickPolygonFn={(homeId, selectedBId, ...prp) => {
                        // toggleHomeItem(homeId, selectedBId, ...prp);
                        dispatch({ type: 'setHomeId', payload: homeId });
                      }}
                    />
                  ) : (
                    <div className='shaxmatka-visual-view-wrapper !h-full'>
                      <span className='no-data-found-wrapper h-full'>
                        <i className='bi bi-exclamation-octagon text-xl leading-4 mr-1' />{' '}
                        {t('common.global.noDataFound')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ) : step == 'stages' ? (
              stagesVisualQuery.data && stagesVisualQuery.data?.id ? (
                <StagesView
                  data={stagesVisualQuery.data}
                  selectedStage={selectedStage}
                  selectedBlock={selectedBlock}
                  selectedEntrance={selectedEntrance}
                  backFn={handleBackFromStages}
                  onClickPolygonFn={(value) => {
                    setSelectedStage(value);
                    setStep('homes');
                    toggleHomeItem(null, null);
                  }}
                />
              ) : (
                <div className='shaxmatka-visual-view-wrapper !h-full'>
                  <span className='no-data-found-wrapper h-full'>
                    <i className='bi bi-exclamation-octagon text-xl leading-4 mr-1' />{' '}
                    {t('common.global.noDataFound')}
                  </span>
                </div>
              )
            ) : step == 'entrances' ? (
              entrancesVisualQuery.data && entrancesVisualQuery.data?.id ? (
                <EntrancesView
                  data={entrancesVisualQuery.data}
                  selectedStage={selectedStage}
                  selectedBlock={selectedBlock}
                  backFn={() => {
                    setSelectedEntrance(null);
                    setStep('blocks');
                  }}
                  onClickPolygonFn={(value) => {
                    setSelectedEntrance({
                      id: value?.id,
                      name: value?.padezd,
                    });
                    setStep('stages');
                  }}
                />
              ) : (
                <div className='shaxmatka-visual-view-wrapper !h-full'>
                  <span className='no-data-found-wrapper h-full'>
                    <i className='bi bi-exclamation-octagon text-xl leading-4 mr-1' />{' '}
                    {t('common.global.noDataFound')}
                  </span>
                </div>
              )
            ) : blocksVisualQuery.data && blocksVisualQuery.data?.id ? (
              <BlocksView
                data={blocksVisualQuery.data}
                selectedBlockId={selectedBlock?.id}
                onClickPolygonFn={(value) => {
                  setSelectedBlock({ id: value?.id, name: value?.block });
                  setStep(value?.padezdimage ? 'entrances' : 'stages');
                }}
              />
            ) : (
              <div className='shaxmatka-visual-view-wrapper !h-full'>
                <span className='no-data-found-wrapper h-full'>
                  <i className='bi bi-exclamation-octagon text-xl leading-4 mr-1' />{' '}
                  {t('common.global.noDataFound')}
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
export default memo(ShaxmatkaVisualView);
